import { Alert, Button, CircularProgress, Container, IconButton, TextField } from '@mui/material';
import { NavLink, useParams } from "react-router-dom";
import { useEffect, useState } from 'react';

import { ICustomerLocation } from '../../../types/customer';
import { IDocument } from '../../../types/document';
import { IExpense } from '../../../types/expense';
import { IManifest } from '../../../types/manifest';
import { IMaterialPrice } from '../../../types/material';
import { ISelectOption } from '../../../types/list';
import { IShipmentExpensePayee, IShipmentLight, IShipmentCommentRequest, IShipmentMaterial, IPinkSheetData, IShipmentArap, IShipmentExpense } from "../../../types/shipment";
import { ITransporterLight } from '../../../types/transporter';
import { IVendor, IVendorLight, IVendorTransporterValueSets } from '../../../types/vendor';

import { ErrorProvider } from '../../../classes/errorProvider';
import { useListContext } from '../../../contexts/listContext';
import { useUserContext } from "../../../contexts/userContext";

import { CustomerService } from '../../../services/customerService';
import { DocumentService } from '../../../services/documentService';
import { ExpenseService } from '../../../services/expenseService';
import { MaterialPriceService } from '../../../services/materialPriceService';
import { ManifestService } from '../../../services/manifestService';
import { ShipmentService } from "../../../services/shipmentService";
import { TransporterService } from '../../../services/transporterService';
import { VendorService } from '../../../services/vendorService';
import { UtilityService } from '../../../services/utilityService';

import PermissionGate from "../../permissionGate";
import Navigation from "../../header";
import ShipmentDetailsForm from '../../forms/shipmentDetailsForm';
import DocumentTable from '../../tables/documentTable';
import DocumentViewer from '../../documentViewer';
import ShipmentArapForm from '../../forms/shipmentArapForm';
import { Icons } from '../../icons';
import ShipmentMaterialsForm from '../../forms/shipmentMaterialsForm';
import ShipmentManifestsForm from '../../forms/shipmentManifestsForm';
import ShipmentExpensesForm from '../../forms/shipmentExpensesForm';
import ShipmentHistoryForm from '../../forms/shipmentHistoryForm';
import ShipmentMaterialSummaryTable from '../../tables/shipmentMaterialSummaryTable';
import PinkSheet from '../../pinkSheet';
import Spinner from '../../spinner';
import WorkingModal, { IWorkingModalProps } from '../../workingModal';
import Json from '../../json';

import '../../../styles/shipmentEdit.scss';

const ShipmentEdit = () => {
	const userContext = useUserContext();
	const listContext = useListContext();
	let { id } = useParams();
	const [documents, setDocuments] = useState<IDocument[]>([]);
	const [activeTab, setActiveTab] = useState("details");
	const [selectedDocumentId, setSelectedDocumentId] = useState<string>();
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [entityTypeId, setEntityTypeId] = useState("");
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [isLoadingDetails, setIsLoadingDetails] = useState(false);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
	const [isLoadingManifests, setIsLoadingManifests] = useState(false);
	const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
	const [isLoadingMaterialPrices, setIsLoadingMaterialPrices] = useState(true);
	const [isLoadingArap, setIsLoadingArap] = useState(false);
	const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
	const [isLoadingShipmentExpenses, setIsLoadingShipmentExpenses] = useState(false);
	const [isLoadingPinkSheetData, setIsLoadingPinkSheetData] = useState(false);
	const [isLoadingManifestVendors, setIsLoadingManifestVendors] = useState(false);
	const [isLoadingSecondaryTransporters, setIsLoadingSecondaryTransporters] = useState(false);
	const [isLoadingPayees, setIsLoadingPayees] = useState(false);
	const [isLoadingManifestWarnings, setIsLoadingManifestWarnings] = useState(false);
	const [isLoadingCustomerLocations, setIsLoadingCustomerLocations] = useState(false);
	const [isLoadingVendor, setIsLoadingVendor] = useState(true);
	const [isSpShipment, setIsSpShipment] = useState<boolean | undefined>(undefined);
	const [expenses, setExpenses] = useState<IExpense[]>([]);
	const [manifests, setManifests] = useState<IManifest[]>([]);
	const [shipmentStatus, setShipmentStatus] = useState("");
	const [shipmentDetails, setShipmentDetails] = useState<IShipmentLight | undefined>(undefined);
	const [shipmentMaterials, setShipmentMaterials] = useState<IShipmentMaterial[]>([]);
	const [shipmentExpenses, setShipmentExpenses] = useState<IShipmentExpense[]>([]);
	const [shipmentArap, setShipmentArap] = useState<IShipmentArap | undefined>(undefined);
	const [materialPrices, setMaterialPrices] = useState<IMaterialPrice[]>([]);
	const [pinkSheetData, setPinkSheetData] = useState<IPinkSheetData | undefined>(undefined);
	const [manifestVendors, setManifestVendors] = useState<ISelectOption[]>([]);
	const [secondaryTransporters, setSecondaryTransporters] = useState<ITransporterLight[]>([]);
	const [customerLocations, setCustomerLocations] = useState<ICustomerLocation[]>([]);
	const [isLoadingVendorsAndTransporters, setIsLoadingVendorsAndTransporters] = useState(true);
	const [vendorTransporterValueSets, setVendorTransporterValueSets] = useState<IVendorTransporterValueSets | undefined>(undefined);
	const [payees, setPayees] = useState<IShipmentExpensePayee[]>([]);
	const [workflowComment, setWorkflowComment] = useState('');
	const [batchStatus, setBatchStatus] = useState<string | undefined>();
	const [isUpdatingDocumentWorkflow, setIsUpdatingDocumentWorkflow] = useState(false);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [vendor, setVendor] = useState<IVendor | undefined>(undefined);

	useEffect(() => {
		getExpenses();
		getSecondaryTransporters();
	}, [])

	useEffect(() => {
		if (shipmentMaterials) {
			shipmentMaterials.some(m => m.vendorMaterialName?.toLowerCase().includes("sp")) ? setIsSpShipment(true) : setIsSpShipment(false);
		}
		else {
			setIsSpShipment(undefined);
		}
	}, [shipmentMaterials])

	useEffect(() => {
		let et = listContext.entityTypes.filter((item) => { return item.name.toLowerCase() == "shipment" })
		if (et.length > 0) {
			setEntityTypeId(et[0].value);
		}

		getShipment();
		getShipmentMaterials();
		getManifests();
		getDocuments();
		getPinkSheetData();
	}, [id])

	useEffect(() => {
		if (shipmentDetails) {
			getVendor(shipmentDetails.vendorId);
			getVendorTransporterValueSets();
			getMaterialPrices();
			getManifestVendors();
			getShipmentExpenses();
			getPayees();
			getArap();
			getCustomerLocations(shipmentDetails.customerId);
		}
	}, [shipmentDetails])

	useEffect(() => {
		if (!isUpdatingDocumentWorkflow) {
			setWorkingModalParams(undefined);
		}
	}, [isUpdatingDocumentWorkflow])

	const getShipment = async () => {
		ShipmentService.getShipmentLight(id)
			.then((response) => {
				console.log('ShipmentService.getShipmentLight: SUCCESS', response);
				const shipment = ShipmentService.mapShipmentLight(response.data);
				setShipmentDetails(shipment);
				setShipmentStatus(shipment.batchStatus || shipment.status);
				setBatchStatus(shipment.batchStatus);
			})
			.catch((error) => {
				console.log('ShipmentService.getShipmentLight: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingDetails(false);
				setIsInitialLoad(false);
			});
	}

	const getShipmentMaterials = () => {
		setIsLoadingMaterials(true);

		ShipmentService.getMultipleShipmentsMaterials([parseInt(id)])
			.then((response) => {
				console.log('ShipmentService.getMultipleShipmentsMaterials: SUCCESS', response);
				const newMaterials = ShipmentService.mapShipmentMaterials(response.data);
				setShipmentMaterials(newMaterials);
			})
			.catch((error) => {
				console.log('ShipmentService.getMultipleShipmentsMaterials: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingMaterials(false);
			});
	}

	const getPinkSheetData = () => {
		setIsLoadingPinkSheetData(true);
		ShipmentService.getPinkSheetData([+id])
			.then((response) => {
				console.log('ShipmentService.getPinkSheetData: SUCCESS', response);
				setPinkSheetData(response.data[0]);
			})
			.catch((error) => {
				console.log('ShipmentService.getPinkSheetData: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingPinkSheetData(false);
			});
	}

	const getDocuments = async () => {
		if (id) {
			setIsLoadingDocuments(true);
			ShipmentService.getShipmentDocuments(id)
				.then((response) => {
					if (response?.data) {
						console.log('ShipmentService.getShipmentDocuments: SUCCESS', response);
						if (selectedDocumentId && !response.data.find(doc => doc.id.toString() == selectedDocumentId)) {
							setSelectedDocumentId(undefined);
						}
						setDocuments(DocumentService.mapDocuments(response.data));
					}
					else {
						throw new Error(`The response did not contain data: [${response.status}] ${response.statusText}`);
					}
				})
				.catch((error) => {
					console.log('ShipmentService.getShipmentDocuments: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsLoadingDocuments(false);
				});
		}
	}

	const getVendorTransporterValueSets = () => {
		setIsLoadingVendorsAndTransporters(true);
		VendorService.getVendorTransporterValueSetsByCustomerId(+shipmentDetails.customerId, +shipmentDetails.vendorId)
			.then((response) => {
				console.log('VendorService.getVendorTransporterValueSets: SUCCESS', response)
				setVendorTransporterValueSets(response.data);
			})
			.catch((error) => {
				console.log('VendorService.getVendorTransporterValueSets: ERROR', error)
			})
			.finally(() => {
				setIsLoadingVendorsAndTransporters(false);
			});
	}

	const getMaterialPrices = () => {
		setIsLoadingMaterialPrices(true);

		MaterialPriceService.getMaterialPricesByShipment(shipmentDetails.id.toString())
			.then((response) => {
				console.log('MaterialService.getMaterialPricesByShipment: SUCCESS', response);
				setMaterialPrices(MaterialPriceService.mapMaterialPrices(response.data).filter(mp => !mp.endDate).sort((a, b) => a.materialName > b.materialName ? 1 : -1));
			})
			.catch((error) => {
				console.log('MaterialService.getMaterialPricesByShipment: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingMaterialPrices(false);
			});
	}

	const getManifests = async () => {
		setIsLoadingManifests(true);

		ManifestService.getManifestsByShipment(id)
			.then((response) => {
				console.log('ManifestService.getManifestsByShipment: SUCCESS', response)
				setManifests(ManifestService.mapManifests(response.data).sort((a, b) => UtilityService.compareStrings(a.number, b.number)));
			})
			.catch((error) => {
				console.log('ManifestService.getManifestsByShipment: ERROR', error)
			})
			.finally(() => {
				setIsLoadingManifests(false);
			});
	}

	const getManifestVendors = async () => {
		setIsLoadingManifestVendors(true);

		VendorService.getVendorsByCustomerId(+shipmentDetails.customerId)
			.then((response) => {
				console.log('VendorService.getVendorsByCustomerId: SUCCESS', response)
				setManifestVendors((response.data as IVendorLight[]).map(v => ({ name: v.name, value: v.id.toString() })).sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
			})
			.catch((error) => {
				console.log('VendorService.getVendorsByCustomerId: ERROR', error)
			})
			.finally(() => {
				setIsLoadingManifestVendors(false);
			});
	}

	const getSecondaryTransporters = async () => {
		setIsLoadingSecondaryTransporters(true);

		TransporterService.getTransporters()
			.then((response) => {
				console.log('TransporterService.getTransporters: SUCCESS', response)
				setSecondaryTransporters(
					TransporterService.mapTransporterLightList(response.data)
						.filter((item) => item.status)
						.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
			})
			.catch((error) => {
				console.log('TransporterService.getTransporters: ERROR', error)
			})
			.finally(() => {
				setIsLoadingSecondaryTransporters(false);
			});
	}

	const getExpenses = () => {
		setIsLoadingExpenses(true);

		ExpenseService.getExpenses()
			.then((response) => {
				console.log('ExpenseService.getExpenses: SUCCESS', response);
				setExpenses(response.data);
			})
			.catch((error) => {
				console.log('ExpenseService.getExpenses: ERROR', error);
			})
			.finally(() => {
				setIsLoadingExpenses(false);
			});
	}

	const getShipmentExpenses = () => {
		setIsLoadingShipmentExpenses(true);

		ShipmentService.getShipmentExpenses(shipmentDetails.id.toString())
			.then((response) => {
				console.log('ShipmentService.getShipmentExpenses: SUCCESS', response);
				const expenses: IShipmentExpense[] = [];
				let totalExpenseAmount = 0;

				response.data.map((item) => {
					let e: IShipmentExpense = {
						amount: item.amount,
						id: item.id,
						shipmentId: item.shipmentId,
						typeId: item.typeId,
						typeName: item.typeName,
						payeeId: item.payeeId,
						payeeName: item.payeeName,
						payeeType: item.payeeType,
						notes: item.notes,
						isCost: item.isCost,
						manifestId: item.manifestId,
						manifestNumber: item.manifestNumber,
					}

					expenses.push(e);
					if (item.isCost) {
						totalExpenseAmount = totalExpenseAmount - item.amount;
					}
					else {
						totalExpenseAmount = totalExpenseAmount + item.amount;
					}
				})
				setShipmentExpenses(expenses.sort((a, b) => UtilityService.compareStrings(a.typeName, b.typeName)));
			})
			.catch((error) => {
				console.log('ShipmentService.getShipmentExpenses: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingShipmentExpenses(false);
			});
	}

	const getArap = () => {
		setIsLoadingArap(true);

		ShipmentService.getShipmentArap(shipmentDetails?.id)
			.then((response) => {
				console.log('ShipmentService.getShipmentArap: SUCCESS', response);
				let newFields: IShipmentArap = {
					invoiceDate: response.data.invoiceDate,
					invoiceNotes: response.data.invoiceNotes,
					transporterInvoice: response.data.transporterInvoice,
					vendorInvoice: response.data.vendorInvoice
				}
				setShipmentArap(newFields);
			})
			.catch((error) => {
				console.log('ShipmentService.getShipmentArap', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsLoadingArap(false);
			});
	}

	const getPayees = () => {
		setIsLoadingPayees(true);

		ShipmentService.getShipmentExpensePayees(shipmentDetails.customerId)
			.then((response) => {
				console.log('ShipmentService.getShipmentExpensePayees: SUCCESS', response);
				setPayees(response.data);
			})
			.catch((error) => {
				console.log('ShipmentService.getShipmentExpensePayees: ERROR', error);
				setErrorMessage("We encountered an error while loading shipment expenses. Please try again.");
			})
			.finally(() => {
				setIsLoadingPayees(false);
			})
	};

	const getCustomerLocations = async (customerId) => {
		setIsLoadingCustomerLocations(true);

		CustomerService.getCustomerLocationsByCustomerId(customerId)
			.then((response) => {
				console.log('CustomerService.getCustomerLocationsByCustomerId: SUCCESS', response)
				setCustomerLocations(CustomerService.mapCustomerLocations(response.data));
			})
			.catch((error) => {
				console.log('CustomerService.getCustomerLocationsByCustomerId: ERROR', error)
			})
			.finally(() => {
				setIsLoadingCustomerLocations(false);
			});
	}

	const getVendor = (vendorId: string) => {
		VendorService.getOnlyVendor(vendorId)
			.then((response) => {
				console.log('VendorService.getOnlyVendor: SUCCESS', response);
				setVendor(VendorService.mapVendor(response.data));
			})
			.catch((error) => {
				console.log('VendorService.getOnlyVendor: ERROR', error);
			})
			.finally(() => {
				setIsLoadingVendor(false);
			});
	}

	const handleWorkflowButtonClick = (id: string, status: string) => {
		setErrorMessage(undefined);
		setIsUpdatingDocumentWorkflow(true);

		setWorkingModalParams({
			params: {
				content:
					<div className="workflow-update">
						<div className="header">Updating Workflow:</div>
						<div className="content">
							<>{shipmentStatus} <Icons.ArrowRight height="32px" className="forward"></Icons.ArrowRight> {status}</>
						</div>
					</div>
			}
		});

		const request: IShipmentCommentRequest = {
			comment: workflowComment
		}

		switch (status.toLowerCase()) {
			case "reconciling": {
				ShipmentService.setStatusReconciling(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusReconciling: SUCCESS', response);
						setShipmentStatus('Reconciling');
						setWorkflowComment('');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusReconciling: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsUpdatingDocumentWorkflow(false);
					});
				break;
			}
			case "escalated": {
				ShipmentService.setStatusEscalated(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusEscalated: SUCCESS', response);
						setShipmentStatus('Escalated');
						setWorkflowComment('');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusEscalated: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsUpdatingDocumentWorkflow(false);
					});
				break;
			}
			case "reviewing": {
				ShipmentService.setStatusReviewing(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusReviewing: SUCCESS', response);
						setShipmentStatus('Reviewing');
						setWorkflowComment('');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusReviewing: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsUpdatingDocumentWorkflow(false);
					});
				break;
			}
			case "auditing": {
				ShipmentService.setStatusAuditing(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusAuditing: SUCCESS', response);
						setShipmentStatus('Auditing');
						setWorkflowComment('');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusAuditing: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsUpdatingDocumentWorkflow(false);
					});
				break;
			}
			case "qb entry": {
				ShipmentService.setStatusQBEntry(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusQBEntry: SUCCESS', response);
						setShipmentStatus('QB Entry');
						setWorkflowComment('');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusQBEntry: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsUpdatingDocumentWorkflow(false);
					});
				break;
			}
			case "qb audit": {
				ShipmentService.setStatusQBAudit(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusQBAudit: SUCCESS', response);
						setShipmentStatus('QB Audit');
						setWorkflowComment('');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusQBAudit: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsUpdatingDocumentWorkflow(false);
					});
				break;
			}
			case "complete": {
				ShipmentService.setStatusComplete(id, request)
					.then((response) => {
						console.log('ShipmentService.setStatusComplete: SUCCESS', response);
						setShipmentStatus('Complete');
						setWorkflowComment('');
					})
					.catch((error) => {
						console.log('ShipmentService.setStatusComplete: ERROR', error);
						setErrorMessage(ErrorProvider.getError(error));
					})
					.finally(() => {
						setIsUpdatingDocumentWorkflow(false);
					});
				break;
			}
			default: {
				// Do nothing
				setIsUpdatingDocumentWorkflow(false);
				break;
			}
		}
	}

	const handleRejectButtonClick = (id: string) => {
		setIsUpdatingDocumentWorkflow(true);

		setWorkingModalParams({
			params: {
				content:
					<div className="workflow-update">
						<div className="header">Updating Workflow:</div>
						<div className="content">
							<>Reconciling <Icons.ArrowRight height="32px" className="back"></Icons.ArrowRight> {shipmentStatus}</>
						</div>
					</div>
			}
		});

		let request: IShipmentCommentRequest = {
			comment: workflowComment
		}

		ShipmentService.setStatusReconciling(id, request)
			.then((response) => {
				console.log('ShipmentService.setStatusReconciling: SUCCESS', response);
				setWorkflowComment('');
				setShipmentStatus('Reconciling');
			})
			.catch((error) => {
				console.log('ShipmentService.setStatusReconciling: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsUpdatingDocumentWorkflow(false);
			});
	}

	const handleShipmentStatusUpdate = (status: string) => {
		// Update shipmentDetails with the same object so it doesn't trigger the useEffect to reload all its dependencies
		shipmentDetails.status = status;
		setShipmentDetails(shipmentDetails);

		// Tabs that require status update notifications can use this instead
		setShipmentStatus(status);
	}

	const handleDocumentDeleted = (id: number) => {
		setDocuments(documents.filter(doc => doc.id !== id));
	}

	const handleMaterialsReloadRequest = () => {
		getShipmentMaterials();
		getMaterialPrices();
	}

	const handleManifestsUpdated = (manifests: IManifest[]) => {
		setManifests(manifests.sort((a, b) => UtilityService.compareStrings(a.number, b.number)));
		getShipmentExpenses();
	}

	const handleMaterialsUpdated = (materials: IShipmentMaterial[]) => {
		setShipmentMaterials(materials);
	}

	const handleExpensesUpdated = (expenses: IShipmentExpense[]) => {
		setShipmentExpenses(expenses);

		if (pinkSheetData) {
			setPinkSheetData({ ...pinkSheetData, ['shipment']: { ...pinkSheetData.shipment, ['expenses']: expenses } });
		}
	}

	const handleArapUpdated = (arap: IShipmentArap) => {
		setShipmentArap(arap);

		if (pinkSheetData) {
			setPinkSheetData({ ...pinkSheetData, ['shipment']: { ...pinkSheetData.shipment, ['arap']: arap } });
		}
	}

	const handleOnTabChangeRequest = (tab: string) => {
		setActiveTab(tab);
	}

	const showWFComment = () => {
		if (shipmentStatus.toLowerCase() == 'escalated' && !userContext.permissions.canApproveEscalatedShipments) return false;
		if (shipmentStatus.toLowerCase() == 'reviewing' && !userContext.permissions.canReviewShipments) return false;
		if (shipmentStatus.toLowerCase() == 'auditing' && !userContext.permissions.canCompleteShipments) return false;
		if (shipmentStatus.toLowerCase() == 'qb entry' && !userContext.permissions.canQBEnter) return false;
		if (shipmentStatus.toLowerCase() == 'qb audit' && !userContext.permissions.canQBAudit) return false;
		if (shipmentStatus.toLowerCase() == 'complete' && !userContext.permissions.canUnlockFinalizedShipments) return false;

		return true;
	}

	return (
		<div className="hwi-shipment-edit">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageShipments}>
					<Spinner dependency={isInitialLoad && (isLoadingDetails || isLoadingVendorsAndTransporters || isLoadingVendor)} text='Loading shipment...'>
						{(id) &&
							<>
								<section className="management-page-header">
									<div className="title">
										<NavLink to="/shipment">
											<Icons.GoBack fill="#9399ad" height="24px"></Icons.GoBack>
										</NavLink>
										Edit Shipment
										{shipmentDetails?.isLegacy &&
											<Icons.LegacyData fill="#A84825" height="12px"></Icons.LegacyData>
										}
									</div>
									<div className="tabs">
										<div className={activeTab == "details" ? "tab active" : "tab"} onClick={() => { setActiveTab("details"); }}>Details</div>
										<div className={activeTab == "expenses" ? "tab active" : "tab"} onClick={() => setActiveTab("expenses")}>Expenses</div>
										<div className={activeTab == "materials" ? "tab active" : "tab"} onClick={() => setActiveTab("materials")}>Materials</div>
										<div className={activeTab == "manifests" ? "tab active" : "tab"} onClick={() => setActiveTab("manifests")}>Manifests</div>
										<div className={activeTab == "arap" ? "tab active" : "tab"} onClick={() => setActiveTab("arap")}>AR / AP</div>
										<div className={activeTab == "history" ? "tab active" : "tab"} onClick={() => setActiveTab("history")}>History</div>
										<div className={activeTab == "summary" ? "tab active" : "tab"} onClick={() => setActiveTab("summary")}>Summary</div>
										<div className={activeTab == "documents" ? "tab active" : "tab"} onClick={() => { setSelectedDocumentId(null); setActiveTab("documents"); }}>Documents</div>
									</div>
								</section>
								{shipmentDetails?.isBatched &&
									<section>
										<Alert className="alert-info" severity="info" >
											This shipment is in <NavLink to={"/batch/" + shipmentDetails?.batchId}><Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem> <strong>Batch #{shipmentDetails?.batchId}</strong></NavLink>,
											{batchStatus && <span> with status <strong>{batchStatus}</strong>.</span>}
											{!batchStatus && <span> but with an <strong>unknown</strong> status.</span>}
										</Alert>
									</section>
								}
								{activeTab == "details" &&
									<section>
										<ShipmentDetailsForm
											shipmentDetails={shipmentDetails}
											vendor={vendor}
											isLoadingDetails={isLoadingDetails}
											vendorTransporterValueSets={vendorTransporterValueSets}
											isLoadingVendorsAndTransporters={isLoadingVendorsAndTransporters}
											isLoadingShipmentManifestWarnings={isLoadingManifestWarnings}
										></ShipmentDetailsForm>
									</section>
								}
								{activeTab == "expenses" &&
									<section>
										<ShipmentExpensesForm
											expenses={expenses}
											isLoadingExpenses={isLoadingExpenses}
											shipmentDetails={shipmentDetails}
											isSpShipment={isSpShipment}
											shipmentExpenses={shipmentExpenses}
											isLoadingShipmentExpenses={isLoadingShipmentExpenses}
											manifests={manifests}
											isLoadingManifests={isLoadingManifests}
											payees={payees}
											isLoadingPayees={isLoadingPayees}
											isLoadingManifestWarnings={isLoadingManifestWarnings}
											onExpensesUpdated={handleExpensesUpdated}
										></ShipmentExpensesForm>
									</section>
								}
								{activeTab == "materials" &&
									<section>
										<ShipmentMaterialsForm
											shipment={shipmentDetails}
											materials={shipmentMaterials || []}
											isLoadingMaterials={isLoadingMaterials}
											manifests={manifests}
											isLoadingManifests={isLoadingManifests}
											materialPrices={materialPrices}
											isLoadingMaterialPrices={isLoadingMaterialPrices}
											isLoadingManifestWarnings={isLoadingManifestWarnings}
											customerLocations={customerLocations}
											isLoadingCustomerLocations={isLoadingCustomerLocations}
											shipmentStatus={shipmentStatus}
											onShipmentStatusUpdate={handleShipmentStatusUpdate}
											onMaterialsReloadRequest={handleMaterialsReloadRequest}
											onTabChangeRequest={handleOnTabChangeRequest} />
									</section>
								}
								{activeTab == "manifests" &&
									<section>
										<ShipmentManifestsForm
											shipment={shipmentDetails}
											isLoadingShipment={isLoadingDetails}
											materials={shipmentMaterials || []}
											manifests={manifests}
											isLoadingManifests={isLoadingManifests}
											manifestVendors={manifestVendors}
											isLoadingManifestVendors={isLoadingManifestVendors}
											secondaryTransporters={secondaryTransporters}
											isLoadingSecondaryTransporters={isLoadingSecondaryTransporters}
											manifestsUpdated={handleManifestsUpdated}
											materialsUpdated={handleMaterialsUpdated}
										></ShipmentManifestsForm>
									</section>
								}
								{activeTab == "history" &&
									<section>
										<ShipmentHistoryForm id={id}></ShipmentHistoryForm>
									</section>
								}
								{activeTab == "arap" &&
									<section>
										<ShipmentArapForm
											shipmentDetails={shipmentDetails}
											shipmentArap={shipmentArap}
											isLoadingArap={isLoadingArap}
											onArapUpdated={handleArapUpdated}
										></ShipmentArapForm>
									</section>
								}
								{activeTab == "summary" &&
									<section>
										<ShipmentMaterialSummaryTable
											shipment={shipmentDetails}
											materials={shipmentMaterials || []}
											isLoadingMaterials={isLoadingMaterials}
											materialPrices={materialPrices}
											isLoadingMaterialPrices={isLoadingMaterialPrices}
											pinkSheetData={pinkSheetData}
											isLoadingPinkSheetData={isLoadingPinkSheetData}
										></ShipmentMaterialSummaryTable>
									</section>
								}
								{activeTab == "documents" &&
									<>
										{!batchStatus &&
											<>
												{shipmentStatus?.toLowerCase() == "reconciling" ? (
													<Alert className="alert-error" severity="info" >
														This shipment is in <strong>{shipmentStatus}</strong> status. Please go to the Materials tab to check for warnings before changing this shipment's status.
													</Alert>
												) : (
													<div className="workflow-controls">
														<div className="title"><strong>Status: </strong>{shipmentStatus}</div>
														{showWFComment() &&
															<div className="comment">
																{(shipmentStatus && (shipmentStatus?.toLowerCase() == "legacy" || shipmentStatus?.toLowerCase() == "escalated" || shipmentStatus?.toLowerCase() == "reviewing" || shipmentStatus?.toLowerCase() == "auditing" || shipmentStatus?.toLowerCase() == "complete"))}
																<TextField className="comment-field"
																	name="comment"
																	fullWidth={true}
																	value={workflowComment}
																	multiline={false}
																	rows={1}
																	placeholder='Comments are required for rejecting or unlocking a shipment.'
																	onChange={(event) => { setWorkflowComment(event.target.value) }}
																>
																</TextField>
															</div>
														}
														<div className="actions">
															{!shipmentStatus &&
																<p><CircularProgress color="primary" size={16} /> Checking status...{"  "}</p>
															}
															{shipmentStatus?.toLowerCase() == "legacy" &&
																<Button variant="contained" color='error' onClick={() => handleWorkflowButtonClick(id, "Reconciling")}>Put in Reconciling Status</Button>
															}
															{shipmentStatus?.toLowerCase() == "reconciling" &&
																<p>
																	This shipment is in <strong>{shipmentStatus}</strong> status. Please go to the Materials tab to check for warnings before changing this shipment's status.
																</p>
															}
															{shipmentStatus?.toLowerCase() == "escalated" &&
																<>
																	{userContext?.permissions.canApproveEscalatedShipments ? (
																		<>
																			<Button variant="contained" onClick={() => handleWorkflowButtonClick(id, "Reviewing")}>Submit</Button>
																			<Button variant="contained" color='error' disabled={workflowComment ? false : true} onClick={() => handleRejectButtonClick(id)}>Reject</Button>
																		</>
																	) : (
																		<p className="text-help">
																			* You do not have permission to make changes.
																		</p>
																	)}
																</>
															}
															{shipmentStatus?.toLowerCase() == "reviewing" &&
																<>
																	{userContext?.permissions.canReviewShipments ? (
																		<>
																			<Button variant="contained" onClick={() => handleWorkflowButtonClick(id, "Auditing")}>Approve</Button>
																			<Button variant="contained" color='error' disabled={workflowComment ? false : true} onClick={() => handleRejectButtonClick(id)}>Reject</Button>
																		</>
																	) : (
																		<p className="text-help">
																			* You do not have permission to make changes.
																		</p>
																	)}
																</>
															}
															{shipmentStatus?.toLowerCase() == "auditing" &&
																<>
																	{userContext?.permissions.canCompleteShipments ? (
																		<>
																			<Button variant="contained" onClick={() => handleWorkflowButtonClick(id, "QB Entry")}>Approve</Button>
																			<Button variant="contained" color='error' disabled={workflowComment ? false : true} onClick={() => handleRejectButtonClick(id)}>Reject</Button>
																		</>
																	) : (
																		<p className="text-help">
																			* You do not have permission to make changes.
																		</p>
																	)}
																</>
															}
															{shipmentStatus?.toLowerCase() == "qb entry" &&
																<>
																	{userContext?.permissions.canQBEnter ? (
																		<>
																			<Button variant="contained" onClick={() => handleWorkflowButtonClick(id, "QB Audit")}>Submit</Button>
																			<Button variant="contained" color='error' disabled={workflowComment ? false : true} onClick={() => handleRejectButtonClick(id)}>Reject</Button>
																		</>
																	) : (
																		<p className="text-help">
																			* You do not have permission to make changes.
																		</p>
																	)}
																</>
															}
															{shipmentStatus?.toLowerCase() == "qb audit" &&
																<>
																	{userContext?.permissions.canQBAudit ? (
																		<>
																			<Button variant="contained" onClick={() => handleWorkflowButtonClick(id, "Complete")}>Complete</Button>
																			<Button variant="contained" color='error' disabled={workflowComment ? false : true} onClick={() => handleRejectButtonClick(id)}>Reject</Button>
																		</>
																	) : (
																		<p className="text-help">
																			* You do not have permission to make changes.
																		</p>
																	)}
																</>
															}
															{shipmentStatus?.toLowerCase() == "complete" &&
																<>
																	{userContext?.permissions.canUnlockFinalizedShipments ? (
																		<>
																			<Button variant="contained" color='error' disabled={workflowComment ? false : true} onClick={() => handleRejectButtonClick(id)}>Unlock</Button>
																		</>
																	) : (
																		<p className="text-help">
																			* You do not have permission to make changes.
																		</p>
																	)}
																</>
															}
														</div>
													</div>
												)
												}
												{!shipmentStatus &&
													<div className="workflow-controls">
														<div className="title"><strong>Status: </strong>{shipmentStatus}</div>
														<div className="actions">
															<p><CircularProgress color="primary" size={16} /> Checking status...{"  "}</p>
														</div>
													</div>
												}
											</>
										}
										<Spinner dependency={isLoadingDocuments} text="Loading documents...">
											{errorMessage &&
												<Alert className="alert-error" severity="error" action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>{errorMessage}</Alert>
											}
											<DocumentTable
												data={documents}
												entityId={id}
												entityTypeId={entityTypeId}
												hideAddDocumentsLink={shipmentStatus?.toLowerCase() == "complete"}
												hideDeleteDocumentsLink={shipmentStatus?.toLowerCase() == "complete"}
												allowExpiringDocuments={false}
												onRequestListRefresh={getDocuments}
												onViewDocument={(id) => { setSelectedDocumentId(id?.toString()) }}
												onDocumentDeleted={(id) => { handleDocumentDeleted(id) }}
											></DocumentTable>
											<div className="document-viewer-container">
												{selectedDocumentId ? (
													<DocumentViewer documentId={+selectedDocumentId}></DocumentViewer>
												) : (
													documents.length > 0 && (
														<Alert className="alert" severity="info">
															Select a document to view its contents.
														</Alert>
													)
												)}
											</div>
										</Spinner>
										<div className="pink-sheet-container">
											<div className="pink-sheet-header">
												<div className="title">
													Pink Sheet
												</div>
											</div>
											<Spinner dependency={!shipmentDetails || isLoadingMaterials || isLoadingPinkSheetData} text="Loading Pink Sheet...">
												<div className="pink-sheet-wrapper">
													<PinkSheet shipment={shipmentDetails} materials={shipmentMaterials} isLoadingMaterials={isLoadingMaterials} pinkSheetData={pinkSheetData} isLoadingPinkSheetData={isLoadingPinkSheetData}></PinkSheet>
												</div>
											</Spinner>
										</div>
									</>
								}
							</>
						}
					</Spinner>
				</PermissionGate>
			</Container>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
			{/* <Json data={shipment}></Json> */}
		</div>
	);
};

export default ShipmentEdit;